import * as Sentry from '@sentry/react';
import {
	browserTracingIntegration,
	captureConsoleIntegration,
	init as initSentryClient,
	replayIntegration,
	setTag
} from '@sentry/react';

import { isCIEnvironment, isDevelopmentEnvironment } from 'src/helpers/EnvHelper';
import { Logger } from 'src/services/Logger';
import { buildObservabilityUserTenantContext } from 'src/services/ObservabilityContextHelper';
import { beforeSendDropNoise } from 'src/services/SentryBeforeSend';
import {
	PRODUCT_NAME,
	SENTRY_PLACEHOLDER,
	SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE,
	SENTRY_REPLAY_SESSION_SAMPLE_RATE,
	SENTRY_TRACES_SAMPLE_RATE
} from 'src/services/SentryService.constants';
import { ITenant } from 'src/types/Tenant';
import { IUser } from 'src/types/User';

const isValidDsn = (dsn: string | undefined): boolean =>
	!!dsn && dsn !== SENTRY_PLACEHOLDER && !dsn.startsWith('x');

type TSentryServiceConfig = {
	dsn: string | undefined;
	enabled: boolean;
	environment: string | undefined;
	projectId: string | undefined;
	release: string;
};

class SentryService {
	private initialized = false;

	private readonly config: TSentryServiceConfig;

	constructor(config: TSentryServiceConfig) {
		this.config = config;
	}

	isEnabled(): boolean {
		return this.config.enabled;
	}

	init(): void {
		try {
			if (this.initialized || !this.isEnabled() || !isValidDsn(this.config.dsn)) {
				return;
			}

			initSentryClient({
				dsn: this.config.dsn,
				integrations: [
					browserTracingIntegration(),
					replayIntegration({
						maskAllText: false,
						blockAllMedia: false
					}),
					captureConsoleIntegration({ levels: ['error'] })
				],
				tracesSampleRate: SENTRY_TRACES_SAMPLE_RATE,
				replaysSessionSampleRate: SENTRY_REPLAY_SESSION_SAMPLE_RATE,
				replaysOnErrorSampleRate: SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE,
				release: this.config.release,
				environment: this.config.environment,
				ignoreErrors: ['Non-Error promise rejection captured'],
				beforeSend: beforeSendDropNoise
			});

			setTag('project-id', this.config.projectId);
			setTag('product-name', PRODUCT_NAME);
			this.initialized = true;
		} catch (error) {
			Logger.error('Error initializing Sentry', error);
		}
	}

	initUser(user: IUser, tenant: ITenant): void {
		try {
			const userContext = buildObservabilityUserTenantContext(user, tenant);
			const { userMetadata } = userContext;

			Sentry.setUser({
				...user,
				username: userContext.userName,
				accountPlan: userMetadata.accountPlan,
				companyName: userMetadata.companyName,
				role: userMetadata.role,
				roleType: userMetadata.roleType,
				tenantId: userMetadata.tenant_id,
				tenantName: userMetadata.tenant_name
			});

			if (tenant.id) {
				setTag('tenant_id', tenant.id);
			}

			const tenantName = tenant.companyName ?? tenant.name;

			if (tenantName) {
				setTag('tenant_name', tenantName);
			}
		} catch (error) {
			Logger.error('Error initializing Sentry user', error);
		}
	}
}

const sentryService = new SentryService({
	dsn: import.meta.env.VITE_SENTRY_DSN,
	enabled: !isDevelopmentEnvironment() && !isCIEnvironment(),
	environment: import.meta.env.VITE_RUN_ENV,
	projectId: import.meta.env.VITE_SENTRY_PROJECT_ID,
	// eslint-disable-next-line no-undef
	release: `${UI_VERSION}`
});

export const isSentryEnabled = (): boolean => sentryService.isEnabled();

export const initSentry = (): void => {
	sentryService.init();
};

export const initSentryUser = (user: IUser, tenant: ITenant): void => {
	sentryService.initUser(user, tenant);
};
